import React, {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ClientTask, ClientFile, ClientCommunication, AnalyticsUpload } from "../types";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { N8N_WEBHOOKS } from "../constants";
import { securelyTriggerN8n } from "../lib/n8n";
import { logger } from "../lib/logger";
import { sanitizers } from "../utils/validation";

// Matches the `authenticated` role's 30s statement_timeout: past this the DB has
// already given up, so the portal shows Retry instead of a skeleton forever.
const PORTAL_FETCH_TIMEOUT_MS = 30_000;

// ONE column list / cap for client_tasks, shared by this initial load and
// ClientPortalPage.refetchTasks. They used to drift (initial load dropped is_draft,
// so drafts rendered as published until the first refetch) so both import these.
export const CLIENT_TASKS_SELECT =
  "id, organization_id, section_id, assigned_to, title, description, status, due_date, created_at, " +
  "priority, start_date, parent_task_id, created_by, updated_at, position, metadata, is_draft, " +
  "profiles:assigned_to(full_name), " +
  "client_task_comments(count)";
export const CLIENT_TASKS_LIMIT = 500; // Safety cap: 500 tasks (under PGRST_MAX_ROWS)

// =============================================================================
// ClientPortalContext - Confidential Client Data (Role-Gated)
// =============================================================================
// PURPOSE: Manages client tasks, files, communications, and analytics uploads.
// Only accessible to exec, owner, admin, client roles.
//
// ARCHITECTURE:
// - Role gate: checks userRole on fetch (returns empty arrays if unauthorized)
// - AbortController cleanup prevents race conditions on org/role changes
// - All writes go through N8N webhooks (ADMIN_WORKFLOW) for audit trail
// - Analytics uploads support both Calendly and Webinar CSV files
//
// KEY DECISIONS:
// - Explicit role check: only exec/client can view confidential data
// - Input sanitization: all user input sanitized before N8N webhook submission
// - 10MB limit for CSV uploads: prevents memory issues on large files
// - Async/await on N8N calls: ensures data consistency before refetch
// =============================================================================

interface ClientPortalContextType {
  clientTasks: ClientTask[];
  setClientTasks: React.Dispatch<React.SetStateAction<ClientTask[]>>;
  clientFiles: ClientFile[];
  setClientFiles: React.Dispatch<React.SetStateAction<ClientFile[]>>;
  clientCommunications: ClientCommunication[];
  setClientCommunications: React.Dispatch<React.SetStateAction<ClientCommunication[]>>;
  analyticsUploads: AnalyticsUpload[];
  isLoading: boolean;
  loadError: string | null;
  refetch: () => void;
  fetchAnalyticsUploads: (type: "calendly" | "webinar") => Promise<void>;
  saveAnalyticsUpload: (
    fileName: string,
    description: string,
    type: "calendly" | "webinar",
    content: string
  ) => Promise<void>;
  /** `type` optional: when omitted it is looked up from the loaded upload row. */
  updateAnalyticsUpload: (
    id: string,
    fileName: string,
    description: string,
    type?: "calendly" | "webinar"
  ) => Promise<void>;
  deleteAnalyticsUpload: (id: string) => Promise<void>;
}

export const ClientPortalContext = createContext<ClientPortalContextType | undefined>(undefined);

interface ClientPortalProviderProps {
  children: ReactNode;
  organizationId: string | null;
  userId: string | null;
  userRole: string | null;
  canViewClientPortal: boolean;
}

export const ClientPortalProvider: React.FC<ClientPortalProviderProps> = ({
  children,
  organizationId,
  userId,
  userRole,
  canViewClientPortal,
}) => {
  const [clientTasks, setClientTasks] = useState<ClientTask[]>([]);
  const [clientFiles, setClientFiles] = useState<ClientFile[]>([]);
  const [clientCommunications, setClientCommunications] = useState<ClientCommunication[]>([]);
  const [analyticsUploads, setAnalyticsUploads] = useState<AnalyticsUpload[]>([]);
  // Starts true: the fetch effect runs after the first paint, so `false` here let the
  // portal flash "No tasks, files, or links yet" for a frame before data arrived.
  // Every early return in fetchConfidentialData sets it false, so it can't stick.
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // AbortController for cleanup
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  // Org whose rows are currently in state. On an org switch the old org's rows are
  // cleared before the new fetch, so they can never render under the new org.
  const loadedOrgIdRef = useRef<string | null>(null);
  // Org whose last load finished with no error. A refetch for that org keeps the page
  // mounted (no full-page skeleton), so an open task modal and its drafts survive it.
  const settledOrgIdRef = useRef<string | null>(null);
  // Latest-request-wins: a slow calendly fetch must not land after a webinar fetch
  // (or after an org switch) and replace the list the user is looking at.
  const analyticsReqIdRef = useRef(0);

  // Org switch: clear the old org's rows DURING render, not in the fetch effect, so the
  // first commit with the new org never paints the old org's tasks/files under it.
  const [stateOrgId, setStateOrgId] = useState(organizationId);
  if (stateOrgId !== organizationId) {
    setStateOrgId(organizationId);
    setClientTasks([]);
    setClientFiles([]);
    setClientCommunications([]);
    if (userRole !== "exec") {
      setAnalyticsUploads([]); // exec's analytics list is not org-scoped
      // Invalidate any in-flight old-org fetch HERE, in render, not in an effect.
      // Child effects run before parent effects, so a heatmap page's own refetch
      // would already be in flight and an effect bump would drop it (AR-335).
      analyticsReqIdRef.current++;
    }
    setIsLoading(true);
  }

  // Fetch Confidential Data (Only if user has access)
  const refetch = useCallback(() => {
    setLoadError(null);
    // Abort any in-flight request to prevent race conditions
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    fetchAbortControllerRef.current = new AbortController();
    const signal = fetchAbortControllerRef.current.signal;

    const fetchConfidentialData = async () => {
      // Every early return clears isLoading: a prior aborted run may have left it true,
      // and the portal page gates its whole skeleton on this flag.
      if (!organizationId || !isSupabaseConfigured || !supabase) {
        setClientTasks([]);
        setClientFiles([]);
        setClientCommunications([]);
        setIsLoading(false);
        return;
      }

      // Gate: only roles in showClientPortal (exec, client) can view confidential data
      // canViewClientPortal is derived from useRBAC().showClientPortal in DataContext
      if (!canViewClientPortal) {
        setClientTasks([]);
        setClientFiles([]);
        setClientCommunications([]);
        setIsLoading(false);
        return;
      }

      if (loadedOrgIdRef.current !== organizationId) {
        setClientTasks([]);
        setClientFiles([]);
        setClientCommunications([]);
        loadedOrgIdRef.current = organizationId;
      }

      if (settledOrgIdRef.current !== organizationId) setIsLoading(true);
      // A hung request must end in the Retry banner, not an endless skeleton.
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        if (fetchAbortControllerRef.current?.signal === signal) {
          timedOut = true;
          fetchAbortControllerRef.current.abort();
        }
      }, PORTAL_FETCH_TIMEOUT_MS);
      try {
        logger.debug("Fetching confidential data");

        // Tasks: Join with profiles on assigned_to + nested comment count
        // client_task_comments(count) uses PostgREST nested aggregate for comment_count.
        const tasksReq = supabase
          .from("client_tasks")
          .select(CLIENT_TASKS_SELECT)
          .eq("organization_id", organizationId)
          .eq("is_archived", false) // Keep consistent with refetchTasks in ClientPortalPage
          .order("position", { ascending: true })
          .order("due_date", { ascending: true })
          .limit(CLIENT_TASKS_LIMIT)
          .abortSignal(signal);

        // Files: Join with profiles on uploaded_by
        const filesReq = supabase
          .from("client_files")
          .select(
            "id, organization_id, uploaded_by, file_name, file_type, s3_url, shared_at, profiles:uploaded_by(*)"
          )
          .eq("organization_id", organizationId)
          .order("shared_at", { ascending: false })
          .limit(500) // Safety cap: 500 files
          .abortSignal(signal);

        // Comms: Base table
        const commsReq = supabase
          .from("client_communications")
          .select("id, organization_id, communication_type, link, notes, created_at")
          .eq("organization_id", organizationId)
          .order("created_at", { ascending: false })
          .limit(500) // Safety cap: 500 communications
          .abortSignal(signal);

        const [tasksRes, filesRes, commsRes] = await Promise.all([tasksReq, filesReq, commsReq]);

        if (timedOut) {
          logger.error("Confidential data fetch timed out");
          setLoadError("We couldn't load this workspace. Tap Retry.");
          return;
        }
        // Bail out if request was aborted (user switched orgs)
        if (signal.aborted) {
          logger.debug("Confidential data fetch aborted - org switch detected");
          return;
        }

        // Transform data: Supabase foreign key relations return arrays, but our types expect object | null.
        // client_task_comments(count) returns [{ count: N }] — flatten to comment_count: number.
        if (tasksRes?.data) {
          const transformedTasks = tasksRes.data.map((task: any) => ({
            ...task,
            profiles: Array.isArray(task.profiles) ? task.profiles[0] || null : task.profiles,
            comment_count: Array.isArray(task.client_task_comments)
              ? (task.client_task_comments[0]?.count ?? 0)
              : 0,
            client_task_comments: undefined, // strip the raw nested object; comment_count carries the value
          }));
          setClientTasks(transformedTasks as ClientTask[]);
        }
        if (filesRes?.data) {
          const transformedFiles = filesRes.data.map((file: any) => ({
            ...file,
            profiles: Array.isArray(file.profiles) ? file.profiles[0] || null : file.profiles,
          }));
          setClientFiles(transformedFiles as ClientFile[]);
        }
        if (commsRes?.data) setClientCommunications(commsRes.data as ClientCommunication[]);

        if (tasksRes?.error) {
          logger.error("Tasks Error", tasksRes.error);
          setLoadError("We couldn't load this workspace. Tap Retry.");
        }
        if (filesRes?.error) {
          logger.error("Files Error", filesRes.error);
          setLoadError("We couldn't load this workspace. Tap Retry.");
        }
        if (commsRes?.error) {
          logger.error("Comms Error", commsRes.error);
          setLoadError("We couldn't load this workspace. Tap Retry.");
        }
        if (!tasksRes?.error && !filesRes?.error && !commsRes?.error) {
          settledOrgIdRef.current = organizationId;
        }
      } catch (err) {
        if (signal.aborted && !timedOut) return; // superseded run: the newer run owns state
        logger.error("Error fetching confidential data", err);
        setLoadError("We couldn't load this workspace. Tap Retry.");
      } finally {
        clearTimeout(timeoutId);
        // A superseded run must NOT clear the flag: the newer run is still in flight,
        // and clearing it early renders the previous org's rows. Every newer-run exit
        // path (early return, success, error, timeout) sets the flag itself.
        if (!signal.aborted || timedOut) setIsLoading(false);
      }
    };

    fetchConfidentialData();
    // userRole intentionally excluded: the fetch body branches only on organizationId
    // and canViewClientPortal (the role-derived access gate), never on userRole itself,
    // so it is an unnecessary dep on this useCallback (exhaustive-deps flags it here where
    // it did not on the prior useEffect — useCallback is strict about extra deps).
  }, [organizationId, canViewClientPortal]);

  // Cleanup: abort in-flight request on unmount or dependency change
  useEffect(() => {
    refetch();
    return () => {
      fetchAbortControllerRef.current?.abort();
    };
  }, [refetch]);

  // --- Analytics File Management ---

  // Mirror of analyticsUploads so updateAnalyticsUpload can find an upload's own type
  // without taking the list as a dep (which would churn the context value).
  const analyticsUploadsRef = useRef<AnalyticsUpload[]>([]);
  useEffect(() => {
    analyticsUploadsRef.current = analyticsUploads;
  }, [analyticsUploads]);

  const fetchAnalyticsUploads = useCallback(
    async (type: "calendly" | "webinar") => {
      if (!supabase || !userId) return;
      const isGlobalAdmin = userRole === "exec";
      // No-org early return BEFORE the bump: a bail-out must not invalidate a valid
      // in-flight fetch.
      if (!isGlobalAdmin && !organizationId) return;
      const reqId = ++analyticsReqIdRef.current;

      try {
        let query = supabase.from("analytics_uploads").select("*").eq("upload_type", type);

        // If NOT global admin, filter by current organization
        if (!isGlobalAdmin) {
          query = query.eq("organization_id", organizationId!);
        }

        const { data, error } = await query.order("created_at", { ascending: false }).limit(100); // Safety cap: 100 uploads

        if (error) throw error;
        if (reqId !== analyticsReqIdRef.current) return; // superseded by a newer fetch
        setAnalyticsUploads(data as AnalyticsUpload[]);
      } catch (err) {
        logger.error("Failed to fetch analytics uploads", err);
      }
    },
    [organizationId, userId, userRole]
  );

  const saveAnalyticsUpload = useCallback(
    async (
      fileName: string,
      description: string,
      type: "calendly" | "webinar",
      content: string
    ) => {
      if (!organizationId || !userId) return;
      try {
        // Sanitize inputs
        const sanitizedFileName = sanitizers.sanitizeString(fileName, 255);
        const sanitizedDescription = sanitizers.sanitizeString(description, 1000);
        // Note: content is CSV data, so we preserve it as-is but limit size
        if (content.length > 10_000_000) {
          // 10MB limit for CSV
          throw new Error("File content too large (max 10MB)");
        }

        // Send { action: 'save', type: 'analytics' } to the Master Admin Workflow
        await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
          action: "save",
          type: "analytics",
          organization_id: organizationId,
          file_name: sanitizedFileName,
          description: sanitizedDescription,
          upload_type: type,
          file_content: content, // CSV content preserved
          uploaded_by: userId,
        });

        await new Promise((r) => setTimeout(r, 1000));
        await fetchAnalyticsUploads(type);
      } catch (err) {
        logger.error("Failed to save analytics upload", err);
        throw err;
      }
    },
    [organizationId, userId, fetchAnalyticsUploads]
  );

  const updateAnalyticsUpload = useCallback(
    async (id: string, fileName: string, description: string, type?: "calendly" | "webinar") => {
      try {
        // Sanitize inputs
        const sanitizedFileName = sanitizers.sanitizeString(fileName, 255);
        const sanitizedDescription = sanitizers.sanitizeString(description, 1000);

        // Refetch the upload's OWN list. Hardcoding "calendly" replaced the webinar
        // page's list with the calendly list after a webinar rename.
        const known = analyticsUploadsRef.current.find((u) => u.id === id)?.upload_type;
        const refetchType: "calendly" | "webinar" =
          type ?? (known === "webinar" || known === "calendly" ? known : "calendly");

        await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
          action: "update",
          type: "analytics",
          id: id,
          file_name: sanitizedFileName,
          description: sanitizedDescription,
        });

        await new Promise((r) => setTimeout(r, 500));
        await fetchAnalyticsUploads(refetchType);
      } catch (err) {
        logger.error("Failed to update analytics upload", err);
        throw err;
      }
    },
    [fetchAnalyticsUploads]
  );

  const deleteAnalyticsUpload = useCallback(async (id: string) => {
    try {
      await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
        action: "delete",
        type: "analytics",
        id: id,
      });

      setAnalyticsUploads((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      logger.error("Failed to delete analytics upload", err);
      throw err;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      clientTasks,
      setClientTasks,
      clientFiles,
      setClientFiles,
      clientCommunications,
      setClientCommunications,
      analyticsUploads,
      isLoading,
      loadError,
      refetch,
      fetchAnalyticsUploads,
      saveAnalyticsUpload,
      updateAnalyticsUpload,
      deleteAnalyticsUpload,
    }),
    [
      clientTasks,
      setClientTasks,
      clientFiles,
      setClientFiles,
      clientCommunications,
      setClientCommunications,
      analyticsUploads,
      isLoading,
      loadError,
      refetch,
      fetchAnalyticsUploads,
      saveAnalyticsUpload,
      updateAnalyticsUpload,
      deleteAnalyticsUpload,
    ]
  );

  return (
    <ClientPortalContext.Provider value={contextValue}>{children}</ClientPortalContext.Provider>
  );
};
